import type {
  ClassificationOutput,
  DimensionsReport,
  DoorWallsOutput,
  HistoryResponse,
  MultiagentRunMeta,
  PlanAnnotationsLite,
  PolygonCollection,
  PrimitivesOutput,
  QuantitiesOutput,
  QuantitiesV2Output,
  RoomsOutput,
  RunIndex,
  RunListEntry,
  UserWallsResponse,
} from "./multiagent-types";

const BASE = "/api";

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body !== undefined && init.body !== null;
  const res = await fetch(url, {
    ...init,
    // Sprint Auth (2026-05-05) — Cookie-basierter Login schickt das
    // bidi-takeoff-auth-Cookie nur mit credentials:"include".
    credentials: init?.credentials ?? "include",
    headers: {
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    // Sprint Auth — bei 401 redirect auf Login (außer bei /api/auth/*).
    if (res.status === 401 && !url.includes("/auth/")) {
      const here = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(here)}`;
    }
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  // ---- Multi-Agent-Pipeline-Outputs (.bidi-takeoff/runs/<runId>/*) ----
  multiagentRuns: () =>
    request<{ runs: RunListEntry[] }>(`${BASE}/multiagent-runs`).then((r) => r.runs),
  /** Sprint 0.20 — Frontend-Upload startet einen neuen Run im Hintergrund. */
  multiagentStartRun: async (
    file: File,
    opts?: {
      runId?: string;
      mode?: "auto" | "forceRaster";
      manualScaleRatio?: number;
      /** Sprint A.3 — Plan-Typ-Pflicht. Backend default "grundriss" wenn
       *  nicht gesetzt; bestehende Aufrufer ohne Picker bleiben kompatibel. */
      planTyp?: import("@bidi-takeoff/shared/gewerk-catalog").PlanTyp;
    }
  ) => {
    const fd = new FormData();
    fd.append("pdf", file);
    if (opts?.runId) fd.append("runId", opts.runId);
    if (opts?.mode) fd.append("mode", opts.mode);
    if (opts?.planTyp) fd.append("planTyp", opts.planTyp);
    if (opts?.manualScaleRatio !== undefined) {
      fd.append(
        "manualScale",
        JSON.stringify({ ratio: opts.manualScaleRatio })
      );
    }
    const res = await fetch(`${BASE}/multiagent-runs`, {
      method: "POST",
      body: fd,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`${res.status}: ${text || res.statusText}`);
    }
    return res.json() as Promise<{ runId: string; accepted: true }>;
  },
  /** Sprint 0.20 — User-definierter Maßstab überschreibt we-stamps/Pfad-C-Pin. */
  multiagentSetScale: (
    runId: string,
    body:
      | { mode: "ratio"; ratio: number }
      | {
          mode: "twoPoint";
          from: { x: number; y: number };
          to: { x: number; y: number };
          realDistanceMm: number;
        }
  ) =>
    request<{
      ok: true;
      runId: string;
      previousRatio: number;
      newRatio: number;
      linearFactor: number;
      source: "user";
      recomputeWarning: string | null;
    }>(`${BASE}/multiagent-runs/${runId}/scale`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Sprint 0.20 — Polling-Endpoint für laufende Multi-Agent-Runs. */
  multiagentRunStatus: (runId: string) =>
    request<{
      runId: string;
      phase: "starting" | "running" | "complete" | "error" | "needsScale";
      completedPhases: string[];
      totalPhases: number;
      startedAt: string | null;
      updatedAt: string | null;
      completedAt: string | null;
      error: string | null;
      mode: "auto" | "forceRaster";
      manualScale: { ratio: number; source: "user" } | null;
      pdfFileName: string | null;
    }>(`${BASE}/multiagent-runs/${runId}/status`),
  multiagentRunIndex: (runId: string) =>
    request<RunIndex>(`${BASE}/multiagent-runs/${runId}`),
  multiagentRunFile: <T = unknown>(runId: string, name: string) =>
    request<T>(`${BASE}/multiagent-runs/${runId}/file/${name}`),
  multiagentClassification: (runId: string) =>
    request<ClassificationOutput>(
      `${BASE}/multiagent-runs/${runId}/file/01-classification.json`
    ),
  multiagentPrimitives: (runId: string) =>
    request<PrimitivesOutput>(
      `${BASE}/multiagent-runs/${runId}/file/02-primitives.json`
    ),
  multiagentPolygons: (runId: string) =>
    request<PolygonCollection>(
      `${BASE}/multiagent-runs/${runId}/file/03-polygons.geojson`
    ),
  multiagentDimensionsReport: (runId: string) =>
    request<DimensionsReport>(
      `${BASE}/multiagent-runs/${runId}/file/03-dimensions-report.json`
    ),
  multiagentRooms: (runId: string) =>
    request<RoomsOutput>(`${BASE}/multiagent-runs/${runId}/file/04-rooms.json`),
  /** Sprint Inline-OCR — Plan-Annotations inkl. perRoomInlineText. */
  multiagentAnnotations: (runId: string) =>
    request<PlanAnnotationsLite>(
      `${BASE}/multiagent-runs/${runId}/file/00-plan-annotations.json`
    ),
  /** Sprint Inline-OCR — Re-Run der annotations-ocr Phase. */
  multiagentInlineOcr: (runId: string, force = false) =>
    request<{
      ok: boolean;
      summary: {
        polygonsScanned: number;
        polygonsSkipped: number;
        polygonsOcrCalled: number;
        polygonsWithText: number;
        totalInputTokens: number;
        totalOutputTokens: number;
        errors: Array<{ polygonId: string; message: string }>;
      };
      polygonsWithText: number;
    }>(`${BASE}/multiagent-runs/${runId}/inline-ocr`, {
      method: "POST",
      body: JSON.stringify({ force }),
    }),
  multiagentPlanBitmapUrl: (
    runId: string,
    layer: "all" | "walls" | "helpers" = "all",
    options: { hideFurniture?: boolean } = {}
  ) => {
    const params = new URLSearchParams();
    if (layer !== "all") params.set("layer", layer);
    if (options.hideFurniture) params.set("hideFurniture", "true");
    const qs = params.toString();
    return `${BASE}/multiagent-runs/${runId}/plan-bitmap${qs ? `?${qs}` : ""}`;
  },
  multiagentSplitPolygon: (
    runId: string,
    body: {
      polygonId: string;
      ringA: number[][];
      ringB: number[][];
      areaFractionA: number;
    }
  ) =>
    request<{
      ok: boolean;
      replacedId: string;
      newIds: [string, string];
      newAreas: [number, number];
    }>(`${BASE}/multiagent-runs/${runId}/split-polygon`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  multiagentDoorWalls: (runId: string) =>
    request<DoorWallsOutput>(`${BASE}/multiagent-runs/${runId}/door-walls`),
  multiagentQuantities: (
    runId: string,
    opts?: {
      raumhoehe?: number;
      putzAbzug?: number;
      verschnittBoden?: number;
      verschnittFliesen?: number;
      verschnittRohbau?: number;
    }
  ) => {
    const q = new URLSearchParams();
    if (opts?.raumhoehe != null) q.set("raumhoehe", String(opts.raumhoehe));
    if (opts?.putzAbzug != null) q.set("putzAbzug", String(opts.putzAbzug));
    if (opts?.verschnittBoden != null)
      q.set("verschnittBoden", String(opts.verschnittBoden));
    if (opts?.verschnittFliesen != null)
      q.set("verschnittFliesen", String(opts.verschnittFliesen));
    if (opts?.verschnittRohbau != null)
      q.set("verschnittRohbau", String(opts.verschnittRohbau));
    const qs = q.toString();
    return request<QuantitiesOutput>(
      `${BASE}/multiagent-runs/${runId}/quantities${qs ? `?${qs}` : ""}`
    );
  },
  multiagentQuantitiesXlsxUrl: (
    runId: string,
    opts?: {
      raumhoehe?: number;
      putzAbzug?: number;
      verschnittBoden?: number;
      verschnittFliesen?: number;
      verschnittRohbau?: number;
    }
  ) => {
    const q = new URLSearchParams();
    if (opts?.raumhoehe != null) q.set("raumhoehe", String(opts.raumhoehe));
    if (opts?.putzAbzug != null) q.set("putzAbzug", String(opts.putzAbzug));
    if (opts?.verschnittBoden != null)
      q.set("verschnittBoden", String(opts.verschnittBoden));
    if (opts?.verschnittFliesen != null)
      q.set("verschnittFliesen", String(opts.verschnittFliesen));
    if (opts?.verschnittRohbau != null)
      q.set("verschnittRohbau", String(opts.verschnittRohbau));
    const qs = q.toString();
    return `${BASE}/multiagent-runs/${runId}/quantities/xlsx${qs ? `?${qs}` : ""}`;
  },
  /** Sprint A.10 — Code-getriebene LV-Position-Liste. */
  multiagentQuantitiesV2: (
    runId: string,
    opts?: { raumhoehe?: number; verschnitt?: number }
  ) => {
    const q = new URLSearchParams();
    if (opts?.raumhoehe != null) q.set("raumhoehe", String(opts.raumhoehe));
    if (opts?.verschnitt != null) q.set("verschnitt", String(opts.verschnitt));
    const qs = q.toString();
    return request<QuantitiesV2Output>(
      `${BASE}/multiagent-runs/${runId}/quantities-v2${qs ? `?${qs}` : ""}`
    );
  },
  multiagentMergePolygons: (
    runId: string,
    body: { idA: string; idB: string; mergedRing: number[][] }
  ) =>
    request<{
      ok: boolean;
      mergedId: string;
      replaced: [string, string];
      areaM2: number;
    }>(`${BASE}/multiagent-runs/${runId}/merge-polygons`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Sprint 0.19 — User-Wand-Striche (manuell gezeichnete Wände, die ins
  // Floodfill-Bitmap injiziert werden).
  multiagentUserWalls: (runId: string) =>
    request<UserWallsResponse>(`${BASE}/multiagent-runs/${runId}/user-walls`),
  multiagentAddUserWall: (
    runId: string,
    body: {
      from: { x: number; y: number };
      to: { x: number; y: number };
      rerunPolygons?: boolean;
    }
  ) =>
    request<{
      ok: boolean;
      count: number;
      recomputed: boolean | null;
      recomputeWarning: string | null;
    }>(`${BASE}/multiagent-runs/${runId}/user-walls`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  multiagentDeleteUserWall: (runId: string, index: number) =>
    request<{ ok: boolean; count: number }>(
      `${BASE}/multiagent-runs/${runId}/user-walls/${index}`,
      { method: "DELETE" }
    ),
  // Stufe 1 (Wand-Katalog) — Default-Set + Run-Overrides.
  multiagentWallCatalog: (runId: string) =>
    request<{
      walls: Array<{
        code: string;
        fullCode: string;
        category: "outer" | "inner";
        shortLabel: string;
        description: string;
        bauweise: string;
        tragend: string;
        schichten: string[];
        totalThicknessMmRange: [number, number];
        defaultLayers: Array<{
          gewerk: "rohbau" | "putz" | "trockenbau" | "sonstiges";
          preset: "mauerwerk" | "trockenbau" | "beton" | "custom";
          materialName: string;
          dickeMm: number;
          putzSides?: 1 | 2;
        }>;
        normReferenz: string[];
        einsatz: string;
        defaultFacadeCode?: string;
      }>;
      facades: Array<{
        code: string;
        fullCode: string;
        shortLabel: string;
        description: string;
        geeignetFuer: string[];
        normReferenz: string[];
      }>;
      hasOverrides: boolean;
    }>(`${BASE}/multiagent-runs/${runId}/wall-catalog`),
  // F16 Phase 3 — Auto-T-Stoss-Trennlinien deaktivieren / reaktivieren.
  multiagentDisableAutoTJunction: (
    runId: string,
    id: string,
    body: { rerunPolygons?: boolean } = {}
  ) =>
    request<{
      ok: boolean;
      count: number;
      recomputed: boolean | null;
      recomputeWarning: string | null;
    }>(`${BASE}/multiagent-runs/${runId}/auto-tjunctions/${id}/disable`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  multiagentEnableAutoTJunction: (
    runId: string,
    id: string,
    rerunPolygons = true
  ) =>
    request<{
      ok: boolean;
      count: number;
      recomputed: boolean | null;
      recomputeWarning: string | null;
    }>(
      `${BASE}/multiagent-runs/${runId}/auto-tjunctions/${id}/disable?rerunPolygons=${rerunPolygons}`,
      { method: "DELETE" }
    ),
  // Sprint 0.19 — Polygon-Vertex-Drag: speichert den editierten Ring.
  // Sprint #3 follow-up: optional `rematchWalls` (default: true für HAUS,
  // false für R*-Polygone — siehe Backend-Comment).
  multiagentEditPolygonRing: (
    runId: string,
    body: {
      polygonId: string;
      ring: number[][];
      rematchWalls?: boolean;
    }
  ) =>
    request<{
      ok: boolean;
      polygonId: string;
      areaM2: number;
      perimeterM: number;
      wallsRematched: number;
      wallsRematchUsed: boolean;
      wallsRematchEdgeCount: number;
      recomputed: boolean | null;
      recomputeWarning: string | null;
    }>(`${BASE}/multiagent-runs/${runId}/edit-polygon-ring`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Sprint #3 follow-up — manueller Bulk-/Einzel-Trigger für Wand-Rematch
  // aus Sprint-W-Cluster-Mittellinien. `polygonIds` leer → alle R*-Räume,
  // `["HAUS"]` → nur HAUS, `["R5","R12"]` → ausgewählte Polygone.
  multiagentRematchWalls: (
    runId: string,
    body?: { polygonIds?: string[] }
  ) =>
    request<{
      ok: boolean;
      touchedPolygons: number;
      totalWallsRematched: number;
      results: Array<{
        polygonId: string;
        wallsBefore: number;
        wallsAfter: number;
        edgeCount: number;
        used: boolean;
      }>;
      recomputed: boolean | null;
      recomputeWarning: string | null;
    }>(`${BASE}/multiagent-runs/${runId}/rematch-walls`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    }),
  // Sprint 0.19 — History (Undo) + Reset-to-Pipeline.
  multiagentHistory: (runId: string) =>
    request<HistoryResponse>(`${BASE}/multiagent-runs/${runId}/history`),
  multiagentRestoreSnapshot: (runId: string, snapshotId: string) =>
    request<{
      ok: boolean;
      restoredFrom: string;
      undoSnapshot: string | null;
    }>(`${BASE}/multiagent-runs/${runId}/restore`, {
      method: "POST",
      body: JSON.stringify({ snapshotId }),
    }),
  multiagentResetToPipeline: (runId: string) =>
    request<{ ok: boolean; undoSnapshot: string | null }>(
      `${BASE}/multiagent-runs/${runId}/reset-to-pipeline`,
      { method: "POST" }
    ),
  // Sprint 0.19 — Wand-CRUD pro Polygon.
  multiagentDeleteWall: (
    runId: string,
    polygonId: string,
    wallIdx: number
  ) =>
    request<{ ok: boolean; wallsRemaining: number }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/walls/${wallIdx}`,
      { method: "DELETE" }
    ),
  multiagentPatchWall: (
    runId: string,
    polygonId: string,
    wallIdx: number,
    body: {
      startPt?: { x: number; y: number };
      endPt?: { x: number; y: number };
      materials?: Array<Record<string, unknown>>;
      wallTypeCode?: string | null;
      facadeCode?: string | null;
      /** Sprint A.2 — explizite Code-Liste mit Quelle/Status/Nachweisen.
       *  null löscht; leeres Array setzt explizit eine leere Liste. Wird
       *  vom Backend ohne weitere Validierung in 03-polygons.geojson
       *  durchgereicht. */
      codeAssignments?: import("@bidi-takeoff/shared/gewerk-catalog").CodeAssignment[] | null;
    }
  ) =>
    request<{ ok: boolean; wall: Record<string, unknown> }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/walls/${wallIdx}`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),
  multiagentAddWall: (
    runId: string,
    polygonId: string,
    body: {
      startPt: { x: number; y: number };
      endPt: { x: number; y: number };
      materials?: Array<Record<string, unknown>>;
    }
  ) =>
    request<{
      ok: boolean;
      wallIdx: number;
      wall: Record<string, unknown>;
      mirroredTo: string | null;
      mirroredToList: string[];
    }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/walls`,
      { method: "POST", body: JSON.stringify(body) }
    ),
  // Sprint 0.20 — Tür/Fenster-CRUD pro Polygon (openings[])
  multiagentAddOpening: (
    runId: string,
    polygonId: string,
    body: {
      type: "door" | "window";
      widthM: number;
      heightM?: number;
      bruestungM?: number;
      startPt?: { x: number; y: number };
      endPt?: { x: number; y: number };
      label?: string;
    }
  ) =>
    request<{ ok: boolean; openingIdx: number; opening: Record<string, unknown> }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/openings`,
      { method: "POST", body: JSON.stringify(body) }
    ),
  multiagentPatchOpening: (
    runId: string,
    polygonId: string,
    idx: number,
    body: Partial<{
      widthM: number;
      heightM: number;
      bruestungM: number;
      startPt: { x: number; y: number };
      endPt: { x: number; y: number };
      label: string;
    }>
  ) =>
    request<{ ok: boolean; opening: Record<string, unknown> }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/openings/${idx}`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),
  multiagentDeleteOpening: (runId: string, polygonId: string, idx: number) =>
    request<{ ok: boolean; removed: Record<string, unknown> }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/openings/${idx}`,
      { method: "DELETE" }
    ),
  // Sprint G2.1 (2026-05-02) — Polygon-CodeAssignments CRUD
  multiagentAddPolygonCodeAssignment: (
    runId: string,
    polygonId: string,
    body: {
      code: string;
      source?: string;
      status?: string;
    }
  ) =>
    request<{ ok: boolean; codeAssignment: Record<string, unknown> }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/code-assignments`,
      { method: "POST", body: JSON.stringify(body) }
    ),
  multiagentDeletePolygonCodeAssignment: (
    runId: string,
    polygonId: string,
    id: string
  ) =>
    request<{ ok: boolean; removed: Record<string, unknown> }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/code-assignments/${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  // Sprint G2.1 (2026-05-02) — Sanitär-Objekte CRUD pro Polygon
  multiagentAddSanitaerObjekt: (
    runId: string,
    polygonId: string,
    body: {
      type: "WC" | "WT" | "DUSCHE" | "BADEWANNE" | "URINAL" | "KUECHENZEILE";
      hersteller?: string;
      modell?: string;
      breiteCm?: number;
      tiefeCm?: number;
      hoeheCm?: number;
      bemerkung?: string;
    }
  ) =>
    request<{ ok: boolean; sanitaerObjekt: Record<string, unknown> }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/sanitaer-objekte`,
      { method: "POST", body: JSON.stringify(body) }
    ),
  multiagentPatchSanitaerObjekt: (
    runId: string,
    polygonId: string,
    id: string,
    body: Partial<{
      type: "WC" | "WT" | "DUSCHE" | "BADEWANNE" | "URINAL" | "KUECHENZEILE";
      hersteller: string;
      modell: string;
      breiteCm: number;
      tiefeCm: number;
      hoeheCm: number;
      bemerkung: string;
    }>
  ) =>
    request<{ ok: boolean; sanitaerObjekt: Record<string, unknown> }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/sanitaer-objekte/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),
  multiagentDeleteSanitaerObjekt: (
    runId: string,
    polygonId: string,
    id: string
  ) =>
    request<{ ok: boolean; removed: Record<string, unknown> }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/sanitaer-objekte/${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  // Sprint 0.20 — Polygon-CRUD: einzelnes Polygon löschen oder neues anlegen.
  multiagentDeletePolygon: (runId: string, polygonId: string) =>
    request<{
      ok: boolean;
      polygonsRemaining: number;
      recomputeWarning: string | null;
    }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}`,
      { method: "DELETE" }
    ),
  multiagentClearAllPolygons: (runId: string, keepHaus = false) =>
    request<{
      ok: boolean;
      polygonsBefore: number;
      polygonsRemaining: number;
      recomputeWarning: string | null;
    }>(
      `${BASE}/multiagent-runs/${runId}/polygons${keepHaus ? "?keepHaus=1" : ""}`,
      { method: "DELETE" }
    ),
  multiagentAddPolygon: (
    runId: string,
    body: {
      ring: number[][];
      label?: string;
      din277Main?: string;
      din277Sub?: string;
      id?: string;
      /** HAUS-Polygon erzeugen (Singleton, Container für Außenwände). */
      isBuilding?: boolean;
    }
  ) =>
    request<{
      ok: boolean;
      polygonId: string;
      areaM2: number;
      perimeterM: number;
      recomputeWarning: string | null;
    }>(`${BASE}/multiagent-runs/${runId}/polygons`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // F7 — Suppression-Liste für AUTO-Türen/-Fenster
  multiagentSuppressAutoDoor: (
    runId: string,
    polygonId: string,
    idx: number
  ) =>
    request<{ ok: boolean; suppressedAutoDoors: number[] }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${polygonId}/suppressed-auto-doors`,
      { method: "POST", body: JSON.stringify({ idx }) }
    ),
  multiagentUnsuppressAutoDoor: (
    runId: string,
    polygonId: string,
    idx: number
  ) =>
    request<{ ok: boolean; suppressedAutoDoors: number[] }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${polygonId}/suppressed-auto-doors/${idx}`,
      { method: "DELETE" }
    ),
  multiagentSuppressAutoWindow: (
    runId: string,
    polygonId: string,
    idx: number
  ) =>
    request<{ ok: boolean; suppressedAutoWindows: number[] }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${polygonId}/suppressed-auto-windows`,
      { method: "POST", body: JSON.stringify({ idx }) }
    ),
  multiagentUnsuppressAutoWindow: (
    runId: string,
    polygonId: string,
    idx: number
  ) =>
    request<{ ok: boolean; suppressedAutoWindows: number[] }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${polygonId}/suppressed-auto-windows/${idx}`,
      { method: "DELETE" }
    ),
  // Sprint M (2026-05-03) — Projektmappe-CRUD
  mappenList: () =>
    request<{
      mappen: Array<
        import("./multiagent-types").MappeListEntry
      >;
    }>(`${BASE}/mappen`),
  mappenGet: (mappeId: string) =>
    request<{
      mappe: import("./multiagent-types").Mappe;
      runs: import("./multiagent-types").MappeRunRef[];
    }>(`${BASE}/mappen/${encodeURIComponent(mappeId)}`),
  mappenCreate: (
    body: Partial<Omit<import("./multiagent-types").Mappe, "id" | "created" | "updated">>
  ) =>
    request<{ mappe: import("./multiagent-types").Mappe }>(`${BASE}/mappen`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  mappenPatch: (
    mappeId: string,
    body: Partial<
      Pick<
        import("./multiagent-types").Mappe,
        "name" | "client" | "address" | "architekt" | "bauvorhaben"
      >
    >
  ) =>
    request<{ mappe: import("./multiagent-types").Mappe }>(
      `${BASE}/mappen/${encodeURIComponent(mappeId)}`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),
  mappenDelete: (mappeId: string) =>
    request<{ ok: boolean; detachedRuns: number }>(
      `${BASE}/mappen/${encodeURIComponent(mappeId)}`,
      { method: "DELETE" }
    ),
  multiagentAssignMappe: (
    runId: string,
    body: {
      mappeId?: string | null;
      displayLabel?: string | null;
      sortOrder?: number | null;
    }
  ) =>
    request<{ meta: import("./multiagent-types").MultiagentRunMeta }>(
      `${BASE}/multiagent-runs/${runId}/mappe`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),
  /** Sprint P (2026-05-03) — Cross-Plan-Aggregation pro Mappe. */
  mappenCrossPlanData: (mappeId: string) =>
    request<import("./multiagent-types").MappeCrossPlanData>(
      `${BASE}/mappen/${encodeURIComponent(mappeId)}/cross-plan-data`
    ),

  // Sprint D (2026-05-03) — Polygon-Klassifikation manuell editieren
  // (Label + DIN 277). Speichert ins polygon.properties.userClassification;
  // null/leerer String löscht das jeweilige Override-Feld.
  multiagentPatchPolygonClassification: (
    runId: string,
    polygonId: string,
    body: {
      label?: string | null;
      din277Sub?: string | null;
      din277Main?: "NUF" | "VF" | "TF" | "KF" | null;
      din277Name?: string | null;
    }
  ) =>
    request<{
      ok: boolean;
      userClassification: Record<string, unknown> | null;
    }>(
      `${BASE}/multiagent-runs/${runId}/polygons/${encodeURIComponent(polygonId)}/classification`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),

  // Sprint 0.21 — Run-Meta (Etage + Wohneinheiten)
  multiagentGetMeta: (runId: string) =>
    request<{ meta: MultiagentRunMeta }>(
      `${BASE}/multiagent-runs/${runId}/meta`,
      { method: "GET" }
    ),
  multiagentPatchMeta: (runId: string, body: Partial<MultiagentRunMeta>) =>
    request<{ meta: MultiagentRunMeta }>(
      `${BASE}/multiagent-runs/${runId}/meta`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),

  // Sprint 0.22 — LV-Modul
  featureFlags: () =>
    request<{ lv: boolean }>(`${BASE}/feature-flags`, { method: "GET" }),
  lvTransfer: (
    runId: string,
    body?: {
      settings?: Record<string, unknown>;
      computeOptions?: Record<string, number>;
    }
  ) =>
    request<{
      lvRunId: string;
      project: import("@bidi-takeoff/lv-shared").LvProject;
      warnings: string[];
    }>(`${BASE}/multiagent-runs/${runId}/lv-transfer`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    }),
  lvRunsList: () =>
    request<{
      runs: Array<{
        lvRunId: string;
        sourceRunId: string;
        updatedAt: string;
        positionCount: number;
      }>;
    }>(`${BASE}/lv-runs`, { method: "GET" }),
  lvRunGet: (lvRunId: string) =>
    request<{ project: import("@bidi-takeoff/lv-shared").LvProject }>(
      `${BASE}/lv-runs/${lvRunId}`,
      { method: "GET" }
    ),
  lvRunPatchSettings: (
    lvRunId: string,
    body: Partial<import("@bidi-takeoff/lv-shared").LvProjectSettings>
  ) =>
    request<{ project: import("@bidi-takeoff/lv-shared").LvProject }>(
      `${BASE}/lv-runs/${lvRunId}/settings`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),
  lvRunPatchPosition: (
    lvRunId: string,
    ozAuto: string,
    body: Partial<import("@bidi-takeoff/lv-shared").LvPosition>
  ) =>
    request<{ position: import("@bidi-takeoff/lv-shared").LvPosition }>(
      `${BASE}/lv-runs/${lvRunId}/positions/${encodeURIComponent(ozAuto)}`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),
  lvRunExportUrl: (lvRunId: string, format: "json" | "xlsx") =>
    `${BASE}/lv-runs/${lvRunId}/export?format=${format}`,
  // ===========================================================================
  // LV-Katalog Phase 2 (2026-05-07) — externe Vorschläge aus dreiplus-Korpus.
  // ===========================================================================
  /** Pre-berechnetes Mapping pro bidi-Code (Bulk-Mapper Output). */
  lvKatalogCodeMapping: (bidiCode: string) =>
    request<{
      korpusVersion: string;
      mapping: {
        bidiCode: string;
        gewerkPrefix: string;
        bezeichnung: string;
        coverage: "good" | "weak" | "none";
        vorschlaege: Array<{
          ozKatalog: string;
          hauptkategorie: { code: string; name: string };
          kurztext: string;
          langtextSnippet: string;
          einheit: string;
          confidence: number;
          reasoning: string;
        }>;
        generatedAt: string;
      };
    }>(`${BASE}/lv-katalog/code-mappings/${encodeURIComponent(bidiCode)}`),
  /** Live-Lookup: dynamische Query gegen den Katalog. */
  lvKatalogLookup: (query: {
    gewerk: string;
    einheit?: string;
    dickeMm?: number;
    freitext?: string;
    topK?: number;
  }) =>
    request<{
      treffer: Array<{
        position: {
          ozKatalog: string;
          hauptkategorie: { code: string; name: string };
          kurztext: string;
          langtext: string;
          einheit: string;
        };
        confidence: number;
        reasoning: string;
      }>;
    }>(`${BASE}/lv-katalog/lookup`, {
      method: "POST",
      body: JSON.stringify(query),
    }),
  /** Position-Detail (für „Detail anzeigen"-Modal). */
  lvKatalogPosition: (ozKatalog: string) =>
    request<{
      ozKatalog: string;
      hauptkategorie: { code: string; name: string };
      subkategorie: { code: string; name: string };
      kurztext: string;
      langtext: string;
      langtextIntro: string | null;
      langtextFields: Record<string, string>;
      einheit: string;
      bedarfsposition: boolean;
      source: { ocrEngine: string; ocrConfidence: number; extractedAt: string };
    }>(`${BASE}/lv-katalog/positions/${encodeURIComponent(ozKatalog)}`),
  lvRunDelete: (lvRunId: string) =>
    request<{ ok: boolean }>(`${BASE}/lv-runs/${lvRunId}`, {
      method: "DELETE",
    }),
  // Sprint Z++ (2026-05-05) — Nachweis-File-Upload pro Fachplaner.
  multiagentNachweisUpload: async (
    runId: string,
    body: { caId: string; fachplaner: string; file: File }
  ): Promise<{
    ok: boolean;
    caId: string;
    fachplaner: string;
    fileRef: {
      filename: string;
      sizeBytes: number;
      uploadedAt: string;
      pathInRun: string;
      mimeType?: string;
    };
    status: string;
    location: { polygonId: string; wallIdx: number | null };
  }> => {
    const fd = new FormData();
    fd.append("caId", body.caId);
    fd.append("fachplaner", body.fachplaner);
    fd.append("file", body.file, body.file.name);
    const res = await fetch(
      `${BASE}/multiagent-runs/${runId}/nachweise/upload`,
      { method: "POST", body: fd, credentials: "include" }
    );
    if (!res.ok) {
      if (res.status === 401) {
        const here = window.location.pathname + window.location.search;
        window.location.href = `/login?next=${encodeURIComponent(here)}`;
      }
      const text = await res.text().catch(() => "");
      throw new Error(`${res.status}: ${text || res.statusText}`);
    }
    return res.json();
  },
  multiagentNachweisDelete: (
    runId: string,
    caId: string,
    fachplaner: string
  ) =>
    request<{ ok: boolean }>(
      `${BASE}/multiagent-runs/${runId}/nachweise/${encodeURIComponent(
        caId
      )}/${encodeURIComponent(fachplaner)}`,
      { method: "DELETE" }
    ),
  multiagentNachweisFileUrl: (runId: string, filename: string) =>
    `${BASE}/multiagent-runs/${runId}/nachweise/file/${encodeURIComponent(
      filename
    )}`,
};
